import { useState } from "react";

import { Note } from "./Note";
import { t } from "../i18n/strings";

/** Telegram refuses a longer message, and the api refuses it before trying to send. */
export const MAX_REPLY_LENGTH = 4096;

interface ReplyComposerProps {
  sending: boolean;
  clientBlocked: boolean;
  error: string | null;
  onSend: (text: string, close: boolean) => Promise<boolean>;
  onClose: () => void;
}

export function ReplyComposer({ sending, clientBlocked, error, onSend, onClose }: ReplyComposerProps) {
  const [text, setText] = useState("");
  const [confirmingClose, setConfirmingClose] = useState(false);

  const tooLong = text.length > MAX_REPLY_LENGTH;
  // an empty answer is not an answer, and a request in flight must not be sent a second time
  const canSend = text.trim().length > 0 && !tooLong && !sending;

  async function send(close: boolean) {
    if (!canSend) {
      return;
    }
    const sent = await onSend(text, close);
    if (sent) {
      setText("");
    }
  }

  return (
    <div className="composer">
      {clientBlocked ? <Note kind="warning">{t("reply.blocked")}</Note> : null}
      {error ? <Note kind="error">{error}</Note> : null}

      <textarea
        className="composer__input"
        value={text}
        placeholder={t("reply.placeholder")}
        rows={4}
        disabled={sending}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
            void send(false);
          }
        }}
      />

      <div className="composer__counter">
        {text.length} / {MAX_REPLY_LENGTH}
      </div>
      {tooLong ? <Note kind="error">{t("reply.tooLong", { length: text.length, max: MAX_REPLY_LENGTH })}</Note> : null}

      <div className="composer__buttons">
        <button type="button" className="button button--primary" disabled={!canSend} onClick={() => void send(false)}>
          {sending ? t("reply.sending") : t("reply.send")}
        </button>
        <button type="button" className="button" disabled={!canSend} onClick={() => void send(true)}>
          {t("reply.sendAndClose")}
        </button>
        <button type="button" className="button button--quiet" disabled={sending} onClick={() => setConfirmingClose(true)}>
          {t("reply.close")}
        </button>
      </div>

      {confirmingClose ? (
        <div className="confirm">
          <p className="confirm__question">{t("reply.confirmClose")}</p>
          <div className="confirm__buttons">
            <button
              type="button"
              className="button button--danger"
              disabled={sending}
              onClick={() => {
                setConfirmingClose(false);
                onClose();
              }}
            >
              {t("reply.confirmYes")}
            </button>
            <button type="button" className="button button--quiet" onClick={() => setConfirmingClose(false)}>
              {t("reply.confirmNo")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
